import * as THREE from 'three';
import { TILE, tileKey } from '../../shared/geo.js';
import { MeshBuilder } from '../world/meshbuilder.js';
import { loadModel } from '../world/models.js';

const SHEDS = 4; // open zinc-roofed sheds round a market, from the Blender-made model
const SHED_RADIUS = 34; // metres from the middle
let shedModel = null;
let shedRequested = false;
import { resolveCircle } from '../../shared/collision.js';
import { hash2 } from '../../shared/rng.js';
import { STALLS, stallSpot } from '../../shared/market.js';

const WOOD = '#7a5230';
const UMBRELLAS = ['#c8402f', '#f2b705', '#2f6fb4', '#2f8a56', '#f4f1e8', '#e07a28', '#7a3fa0'];
// tomatoes, plantain, oranges, onions, yams, greens, pepper, garri, groundnuts, cloth
const GOODS = ['#c8402f', '#7fa832', '#e8902a', '#8a3f6a', '#8b6a48', '#3f8a3a', '#d8402f', '#e8dcae', '#b98a52', '#2f6fb4'];
const REACH = 48; // metres from the middle of a market that its stalls may reach

const COLOURS = new Map();
const colour = (hex) => {
  let c = COLOURS.get(hex);
  if (!c) COLOURS.set(hex, (c = new THREE.Color(hex)));
  return c;
};
const UV = [0, 0, 0, 0];

// Writes shapes into a MeshBuilder in a stall's own frame: turned by yaw and moved to (sx, sy, sz).
class StallPen {
  constructor(builder) {
    this.b = builder;
    this.set(0, 0, 0, 0);
  }

  set(sx, sy, sz, yaw) {
    this.sx = sx;
    this.sy = sy;
    this.sz = sz;
    this.cos = Math.cos(yaw);
    this.sin = Math.sin(yaw);
  }

  at(x, y, z) {
    return [this.sx + x * this.cos + z * this.sin, this.sy + y, this.sz - x * this.sin + z * this.cos];
  }

  // a box of w by h by d centred at (x, y, z)
  box(w, h, d, hex, x, y, z) {
    const c = colour(hex);
    const P = (ax, ay, az) => this.at(x + (ax * w) / 2, y + (ay * h) / 2, z + (az * d) / 2);
    const q = (a, b, cc, dd) => this.b.quad(P(...a), P(...b), P(...cc), P(...dd), c, UV);
    q([1, -1, -1], [1, 1, -1], [1, 1, 1], [1, -1, 1]);
    q([-1, -1, 1], [-1, 1, 1], [-1, 1, -1], [-1, -1, -1]);
    q([-1, 1, 1], [1, 1, 1], [1, 1, -1], [-1, 1, -1]);
    q([-1, -1, -1], [1, -1, -1], [1, -1, 1], [-1, -1, 1]);
    q([-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]);
    q([1, -1, -1], [-1, -1, -1], [-1, 1, -1], [1, 1, -1]);
  }

  // a cone of radius r and height h, point up, centred at (x, y, z)
  cone(r, h, hex, x, y, z, segments = 8) {
    const c = colour(hex);
    const top = this.at(x, y + h / 2, z);
    const centre = this.at(x, y - h / 2, z);
    const uv = [0, 0];
    for (let i = 0; i < segments; i++) {
      const a0 = (i / segments) * Math.PI * 2;
      const a1 = ((i + 1) / segments) * Math.PI * 2;
      const p0 = this.at(x + Math.cos(a0) * r, y - h / 2, z + Math.sin(a0) * r);
      const p1 = this.at(x + Math.cos(a1) * r, y - h / 2, z + Math.sin(a1) * r);
      this.b.tri(p1, p0, top, c, uv, uv, uv);
      this.b.tri(p0, p1, centre, c, uv, uv, uv);
    }
  }
}

// The stalls of the town's markets, standing exactly where the simulation's
// traders stand. By day they are laid with goods under bright umbrellas; at
// night and on quiet Mondays only the bare tables are left.
export class Markets {
  constructor(scene, map, chunks) {
    this.scene = scene;
    this.map = map;
    this.chunks = chunks;
    this.material = new THREE.MeshLambertMaterial({ vertexColors: true });
    this.byTile = new Map(); // tile key -> the markets whose middle is in that tile
    for (const p of map.places) {
      if (p.k !== 'market') continue;
      const key = tileKey(Math.floor(p.x / TILE), Math.floor(p.z / TILE));
      if (!this.byTile.has(key)) this.byTile.set(key, []);
      this.byTile.get(key).push({ x: p.x, z: p.z, tables: null, wares: null, sheds: [] });
    }
    this.waiting = new Set(); // markets whose ground is not all loaded yet
    this.landmarks = null; // set by the game: hand-made halls the sheds must keep clear of
    this.open = true;
    this.hits = [];
  }

  // Call when a tile is built: the markets whose middle is in it join the queue.
  addTile(t) {
    for (const m of this.byTile.get(t.key) || []) this.waiting.add(m);
  }

  // The market whose stalls stand within r metres of a point, if it is open and built, else null.
  at(x, z, r = 40) {
    if (!this.open) return null;
    for (const list of this.byTile.values()) for (const m of list) if (m.tables && Math.hypot(m.x - x, m.z - z) < r) return m;
    return null;
  }

  // Call every frame: builds the stalls of at most one market whose ground has all arrived, so a
  // frame never pays for several markets at once.
  update() {
    for (const m of this.waiting) {
      if (!this.ready(m)) continue;
      this.waiting.delete(m);
      this.build(m);
      return;
    }
  }

  removeTile(t) {
    for (const m of this.byTile.get(t.key) || []) {
      this.waiting.delete(m);
      for (const shed of m.sheds) this.scene.remove(shed);
      m.sheds = [];
      for (const mesh of [m.tables, m.wares]) {
        if (!mesh) continue;
        this.scene.remove(mesh);
        mesh.geometry.dispose();
      }
      m.tables = m.wares = null;
    }
  }

  // True once every tile the market's stalls may stand on is built.
  ready(m) {
    for (const dz of [-REACH, REACH]) {
      for (const dx of [-REACH, REACH]) {
        const t = this.chunks.tiles.get(tileKey(Math.floor((m.x + dx) / TILE), Math.floor((m.z + dz) / TILE)));
        if (!t || t.state !== 'built') return false;
      }
    }
    return true;
  }

  build(m) {
    const h = this.map.heightmap;
    const tables = new MeshBuilder();
    const wares = new MeshBuilder();
    const table = new StallPen(tables);
    const ware = new StallPen(wares);
    for (let k = 0; k < STALLS; k++) {
      const s = stallSpot(m.x, m.z, k);
      // no stall in the road, in a stream, against a wall or on a steep bank
      if (this.chunks.surfaceAt(s.x, s.z) !== -1 || this.chunks.wet(s.x, s.z, 2)) continue;
      if (resolveCircle(s.x, s.z, 1.7, this.chunks.collidersNear(s.x, s.z, this.hits)).hit) continue;
      if (h.normalAt(s.x, s.z)[1] < 0.95) continue;
      const y = h.heightAt(s.x, s.z);
      const yaw = Math.atan2(s.fx, s.fz);
      table.set(s.x, y, s.z, yaw);
      ware.set(s.x, y, s.z, yaw);
      table.box(2.2, 0.08, 1, WOOD, 0, 0.8, 0);
      for (const [lx, lz] of [[-1, -0.4], [1, -0.4], [-1, 0.4], [1, 0.4]]) table.box(0.08, 0.8, 0.08, WOOD, lx, 0.4, lz);
      table.box(0.07, 2.4, 0.07, '#3a3a3e', 0, 1.2, -0.62);
      const r = (salt) => hash2(Math.round(s.x * 4), Math.round(s.z * 4), salt);
      ware.cone(1.75, 0.55, UMBRELLAS[Math.floor(r(1) * UMBRELLAS.length)], 0, 2.55, -0.3);
      for (let g = 0; g < 3; g++) {
        const hex = GOODS[Math.floor(r(2 + g) * GOODS.length)];
        ware.box(0.56, 0.16 + r(6 + g) * 0.16, 0.7, hex, -0.72 + g * 0.72, 0.94, 0);
      }
    }
    if (tables.empty) return;
    const mesh = (builder) => {
      const o = new THREE.Mesh(builder.geometry(), this.material);
      o.castShadow = true;
      o.receiveShadow = true;
      this.scene.add(o);
      return o;
    };
    m.tables = mesh(tables);
    m.wares = mesh(wares);
    m.wares.visible = this.open;
    this.buildSheds(m);
  }

  // The sheds stand in a ring outside the stalls, where the ground is free, facing the middle.
  buildSheds(m) {
    if (!shedRequested) {
      shedRequested = true;
      loadModel('market-shed', { scale: 1 }).then((g) => {
        shedModel = g;
      });
    }
    const place = () => {
      if (!shedModel || m.sheds.length) return;
      const h = this.map.heightmap;
      const start = hash2(Math.round(m.x), Math.round(m.z), 71) * Math.PI * 2;
      for (let k = 0; k < SHEDS; k++) {
        const a = start + (k / SHEDS) * Math.PI * 2;
        const x = m.x + Math.cos(a) * SHED_RADIUS;
        const z = m.z + Math.sin(a) * SHED_RADIUS;
        if (this.chunks.surfaceAt(x, z) !== -1 || this.chunks.wet(x, z, 4)) continue;
        if (resolveCircle(x, z, 7.5, this.chunks.collidersNear(x, z, this.hits)).hit) continue;
        if (this.landmarks && this.landmarks.blocks(x, z, 7.5)) continue;
        if (h.normalAt(x, z)[1] < 0.96) continue;
        const shed = new THREE.Mesh(shedModel, this.material);
        shed.position.set(x, h.heightAt(x, z) - 0.05, z);
        shed.rotation.y = Math.atan2(m.x - x, m.z - z);
        shed.castShadow = true;
        shed.receiveShadow = true;
        this.scene.add(shed);
        m.sheds.push(shed);
      }
    };
    if (shedModel) place();
    else loadModel('market-shed', { scale: 1 }).then(() => place());
  }

  // hour 0-24. quiet: a stay-at-home day. The markets trade from early morning to dusk.
  setTime(hour, quiet) {
    const open = hour >= 6.5 && hour < 18.2 && !quiet;
    if (open === this.open) return;
    this.open = open;
    for (const list of this.byTile.values()) for (const m of list) if (m.wares) m.wares.visible = open;
  }
}
